import { styled } from '@mui/material/styles';
import { Box, Divider, Grid } from '@mui/material';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import SelectMenu from '../../forms/SelectMenu';
import ChargeTypeItem from './ChargeTypeItem';
import { TransportationService } from '../../../data/models/transportation-service.model';
import { TransportationAttribute } from '../../../data/models/transportation-attribute.model';
import { PricingModel } from '../../../data/models/pricing-model.model';
import { useEffect, useState } from 'react';
import { uniq } from 'lodash';
import { MultiSelectTree, TreeNode } from '../../forms/MultiSelectTree';

const PREFIX = 'ServiceLevelsForm';

const classes = {
  container: `${PREFIX}-container`,
  form: `${PREFIX}-form`,
  chipContainer: `${PREFIX}-chipContainer`,
  priceHeader: `${PREFIX}-priceHeader`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'flex-start',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },

  [`& .${classes.form}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    width: 500,
  },

  [`& .${classes.chipContainer}`]: {
    display: 'flex',
    flexWrap: 'wrap',
    maxWidth: '456px',
    gap: theme.spacing(0.5),
    paddingTop: theme.spacing(0.375),
    paddingBottom: 0,
  },

  [`& .${classes.priceHeader}`]: {
    marginTop: theme.spacing(1),
    color: theme.palette.lightTextPrimary.main,
    fontWeight: 600,
  },
}));

interface ServiceLevelsFormProps {
  disableServiceLevelSelection: boolean;
  clientServiceLevels: TransportationService[];
  transportationAttributes: TransportationAttribute[];
  pricingModels: PricingModel[];
  formHook: UseFormReturn<any, any>;
}

export default function ServiceLevelsForm({
  disableServiceLevelSelection,
  clientServiceLevels,
  transportationAttributes,
  pricingModels,
  formHook,
}: ServiceLevelsFormProps) {
  const { t } = useTranslation('common');
  const requiredFieldMessage = t('common.requiredField');

  const { control, watch, trigger } = formHook;
  const selectedPricingModelUUID = watch('PricingPlan');
  const [selectedPricingModel, setSelectedPricingModel] =
    useState<PricingModel>();

  useEffect(() => {
    const pm = pricingModels.find(
      (p) => p.pricingModelUUID === selectedPricingModelUUID,
    );
    setSelectedPricingModel(pm);
    setTimeout(() => {
      trigger();
    }, 0);
  }, [selectedPricingModelUUID]);

  const childCategories = uniq(
    transportationAttributes.map(
      (ta) => ta.childTransportationAttributeCategoryBC,
    ),
  );

  const rootTransAttrbs = transportationAttributes.filter(
    (ta) => !childCategories.includes(ta.transportationAttributeCategoryBC),
  );

  const getTreeNode = (
    attrb: TransportationAttribute,
    parentLabel?: string,
    parentId?: string,
  ) => {
    const selectedLabel = parentLabel
      ? `${parentLabel} - ${attrb.name}`
      : attrb.name;
    const id = parentId
      ? `${parentId}:${attrb.transportationAttributeUUID}`
      : (attrb.transportationAttributeUUID ?? '');
    return {
      id,
      label: attrb.name,
      selectedLabel,
      value: id,
      children: getChildAttributes(attrb, selectedLabel, id),
    };
  };

  const getChildAttributes = (
    attrb: TransportationAttribute,
    parentLabel?: string,
    parentId?: string,
  ) => {
    const children = transportationAttributes.filter(
      (ta) =>
        ta.transportationAttributeCategoryBC ===
        attrb.childTransportationAttributeCategoryBC,
    );
    const treeNodes: TreeNode[] = children.map((child) =>
      getTreeNode(child, parentLabel, parentId),
    );
    return treeNodes;
  };

  const transAttrbTree: TreeNode[] = rootTransAttrbs.map((attrb) => {
    return getTreeNode(attrb);
  });

  return (
    <StyledBox className={classes.container}>
      <Box className={classes.form}>
        {clientServiceLevels.length && (
          <SelectMenu
            label={t('client.serviceLevel')}
            name="ServiceLevel"
            values={clientServiceLevels}
            valueAttr="transportationServiceUUID"
            labelAttr="name"
            readOnly={disableServiceLevelSelection}
            required={true}
            control={control}
            rules={{ required: requiredFieldMessage }}
          />
        )}
        <MultiSelectTree
          name="TransportationAttributes"
          label={t('client.transportationAttributes')}
          treeData={transAttrbTree}
          control={formHook.control}
        />
        {pricingModels.length > 0 && (
          <>
            <SelectMenu
              label={t('client.pricingPlan')}
              name="PricingPlan"
              values={pricingModels}
              valueAttr="pricingModelUUID"
              labelAttr="name"
              required={true}
              control={control}
              rules={{ required: requiredFieldMessage }}
            />
            <Grid container spacing={2} className={classes.priceHeader}>
              <Grid item xs={3}>
                {t('client.chargeType')}
              </Grid>
              <Grid item xs={3}>
                {t('client.chargeAmount')}
              </Grid>
              <Grid item xs={3}>
                {t('client.chargeRounding')}
              </Grid>
              <Grid item xs={3}>
                {t('client.chargeCptCode')}
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              {selectedPricingModel?.chargeTypes.map((ct, idx) => (
                <ChargeTypeItem
                  key={idx}
                  chargeType={ct}
                  formHook={formHook}
                  idx={idx}
                  displayInputLabels={false}
                />
              ))}
            </Grid>
          </>
        )}
      </Box>
    </StyledBox>
  );
}
